import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { GemIcon } from '@sorare/core/src/atoms/icons/GemIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { thinMaterial } from '@sorare/core/src/style/utils';

import { PurchasableItemPaymentDialog } from '#src/components/commonCard/BuyPacks/PurchasableItemPaymentDialog/index.tsx';

import type { GemsPack_inGameCurrencyPurchasableItem } from './__generated__/index.graphql.ts';
import ellipse from './assets/ellipse.svg';

const Root = styled(Vertical).attrs({ gap: 2 })<{ background: string }>`
  position: relative;
  border-radius: var(--double-unit);
  padding: var(--intermediate-unit);
  justify-content: stretch;
  text-align: center;
  ${thinMaterial}
  border: 1px solid var(--c-nd-150);
  color: inherit;
  font: inherit;
  cursor: pointer;
  width: 100%;

  &:focus-visible {
    outline: 2px solid var(--c-brand-600);
    outline-offset: 2px;
  }

  &.useInGameCurrencyPack {
    background: no-repeat top / contain url(${props => props.background});
    background-size: contain;
    background-position: top;
    aspect-ratio: 296 / 368;
  }
`;

const Container = styled(Vertical).attrs({ center: true, gap: 2 })`
  flex-grow: 1;
  justify-content: center;
`;

const PictureWrapper = styled(Vertical).attrs({ center: true })`
  position: relative;
  isolation: isolate;
`;

const Ellipse = styled(Img).attrs({ src: ellipse })`
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`;

const Picture = styled(Img)`
  width: 70%;
  max-width: 140px;
  object-fit: cover;
`;

type Props = {
  gemsPack: GemsPack_inGameCurrencyPurchasableItem;
};

export const GemsPack = ({ gemsPack }: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const [dialogOpened, setDialogOpened] = useState(false);
  const { postMessage, isIosApp, isMarketAuthorizedIosApp } = useIsMobileApp();

  const track = useEvents();

  const {
    id: packId,
    lowResPictureUrl,
    bonusAmount,
    baseAmount,
    price: packPrice,
  } = gemsPack;

  const price = toMonetaryAmount(packPrice);

  const { main } = useAmountWithConversion({ monetaryAmount: price });

  const closeDialog = () => {
    setDialogOpened(false);
  };

  if (price.isZero) return null;

  const usesNativeIosPurchase = isIosApp && !isMarketAuthorizedIosApp;

  const handleClick = () => {
    track('Click Gem Package', {
      eurAmount: price.eurCents,
      gemAmount: baseAmount,
      purchasableItemId: packId,
    });
    if (usesNativeIosPurchase) {
      postMessage('buyInGameCurrencyPack', { packId });
    } else {
      setDialogOpened(true);
    }
  };

  return (
    <>
      <Root
        as="button"
        type="button"
        background={lowResPictureUrl || ''}
        onClick={handleClick}
      >
        <Container>
          <PictureWrapper>
            <Ellipse />
            <Picture
              src={lowResPictureUrl || ''}
              alt=""
              width="320"
              height="290"
            />
          </PictureWrapper>

          <Vertical center>
            <Horizontal>
              <BrandS>{baseAmount}</BrandS>
              <GemIcon size={2} />
            </Horizontal>
            {bonusAmount > 0 && (
              <Horizontal>
                <BrandS color="var(--c-green-200)">
                  <FormattedMessage
                    defaultMessage="+{amount} bonus"
                    values={{ amount: bonusAmount }}
                  />
                </BrandS>
              </Horizontal>
            )}
          </Vertical>
        </Container>
        <Button size="small" asSpan>
          {main}
        </Button>
      </Root>
      {!!dialogOpened && (
        <PurchasableItemPaymentDialog
          purchasableItem={gemsPack}
          onClose={closeDialog}
        />
      )}
    </>
  );
};

GemsPack.fragments = {
  inGameCurrencyPurchasableItem: gql`
    fragment GemsPack_inGameCurrencyPurchasableItem on InGameCurrencyPurchasableItem {
      slug
      id
      baseAmount
      bonusAmount
      lowResPictureUrl: pictureUrl(derivative: LOW_RES)
      price {
        ...useMonetaryAmount_monetaryAmount
      }
      ...PurchasableItemPaymentDialog_purchasableItem
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${PurchasableItemPaymentDialog.fragments.purchasableItem}
  ` as TypedDocumentNode<GemsPack_inGameCurrencyPurchasableItem>,
};
